import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, Users, Mail, Palette, Building, Building2, MapPin, Plus, Edit2, Trash2, Send, Webhook, Globe, Eye, EyeOff, RotateCw, Loader2, Upload, Download } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { ResourceManager } from '@/components/features/settings/ResourceManager'
import { CustomFieldsManager } from '@/components/features/settings/CustomFieldsManager'
import { PageLayout } from '@/components/layout/PageLayout'
import { toast } from '@/components/ui/toaster'

// Invite Team Member Dialog
function InviteTeamMemberDialog() {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('member')
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const handleInvite = async () => {
    if (!email) return
    setIsLoading(true)
    try {
      await api.post('/users', { email, name: name || email.split('@')[0], role })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast({ title: 'Invitation sent', description: `Invitation sent to ${email}`, variant: 'success' })
      setOpen(false)
      setEmail('')
      setName('')
      setRole('member')
    } catch (error) {
      toast({ title: 'Failed to send invitation', variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) {
      setEmail('')
      setName('')
      setRole('member')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite Team Member</DialogTitle>
          <DialogDescription>
            Send an invitation to join your team
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="colleague@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              type="text"
              placeholder="Team member name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin - Full access</SelectItem>
                <SelectItem value="recruiter">Recruiter - Manage candidates & jobs</SelectItem>
                <SelectItem value="hiring_manager">Hiring Manager - Review & interview</SelectItem>
                <SelectItem value="member">Member - View only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleInvite} disabled={!email || isLoading}>
            <Send className="h-4 w-4 mr-2" />
            {isLoading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Email Templates Manager
function EmailTemplatesManager() {
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [editedSubject, setEditedSubject] = useState('')
  const [editedBody, setEditedBody] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const { data: apiTemplates, isLoading: templatesLoading, error: templatesError, refetch: refetchTemplates } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => api.get<any[]>('/emails/templates'),
  })

  const defaultTemplates = [
    {
      id: 'application_received',
      name: 'Application Received',
      subject: 'Thank you for applying to {{job_title}}',
      body: `Dear {{candidate_name}},

Thank you for applying to the {{job_title}} position at {{company_name}}.

We have received your application and our team will review it shortly. If your qualifications match our requirements, we will reach out to schedule the next steps.

Best regards,
The {{company_name}} Team`,
    },
    {
      id: 'interview_scheduled',
      name: 'Interview Scheduled',
      subject: 'Interview Scheduled - {{job_title}}',
      body: `Dear {{candidate_name}},

We are pleased to invite you for an interview for the {{job_title}} position.

Date: {{interview_date}}
Time: {{interview_time}}
Type: {{interview_type}}

Please confirm your attendance by replying to this email.

Best regards,
{{interviewer_name}}`,
    },
    {
      id: 'offer_extended',
      name: 'Offer Extended',
      subject: 'Job Offer - {{job_title}} at {{company_name}}',
      body: `Dear {{candidate_name}},

We are thrilled to extend an offer for the {{job_title}} position at {{company_name}}.

We believe you will be a great addition to our team. Please review the attached offer letter and let us know if you have any questions.

Best regards,
The {{company_name}} Team`,
    },
    {
      id: 'rejection',
      name: 'Application Update',
      subject: 'Update on your application to {{company_name}}',
      body: `Dear {{candidate_name}},

Thank you for your interest in the {{job_title}} position at {{company_name}}.

After careful consideration, we have decided to move forward with other candidates whose experience more closely matches our current needs.

We appreciate your time and wish you the best in your job search.

Best regards,
The {{company_name}} Team`,
    },
  ]

  const templates = (apiTemplates && Array.isArray(apiTemplates) && apiTemplates.length > 0) ? apiTemplates : defaultTemplates

  const handleEdit = (template: typeof defaultTemplates[0]) => {
    setSelectedTemplate(template.id)
    setEditedSubject(template.subject)
    setEditedBody(template.body)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await api.patch(`/emails/templates/${selectedTemplate}`, { subject: editedSubject, body: editedBody })
      await refetchTemplates()
      toast({ title: 'Template saved', variant: 'success' })
      setSelectedTemplate(null)
    } catch (error) {
      toast({ title: 'Failed to save template', variant: 'destructive' })
    } finally {
      setIsSaving(false)
    }
  }

  if (templatesLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Email Templates</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 skeleton rounded-md" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (templatesError) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Email Templates</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">Failed to load templates. Using defaults.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Email Templates</CardTitle>
        </div>
        <CardDescription>
          Customize email templates for candidate communications. Use {"{{variable}}"} for dynamic content.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {selectedTemplate ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">
                Editing: {templates.find(t => t.id === selectedTemplate)?.name}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate(null)}>
                Cancel
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={editedSubject}
                onChange={(e) => setEditedSubject(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Email Body</Label>
              <Textarea
                value={editedBody}
                onChange={(e) => setEditedBody(e.target.value)}
                rows={12}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex justify-between items-center">
              <p className="text-xs text-muted-foreground">
                Variables: {"{{candidate_name}}"}, {"{{job_title}}"}, {"{{company_name}}"}, {"{{interview_date}}"}
              </p>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((template) => (
              <div
                key={template.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div>
                  <p className="font-medium">{template.name}</p>
                  <p className="text-sm text-muted-foreground">{template.subject}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleEdit(template)}>
                  <Edit2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Team Tab Content
function TeamTabContent() {
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<any[]>('/users'),
  })

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Team Members</CardTitle>
          </div>
          <CardDescription>
            Manage who has access to your ATS
          </CardDescription>
        </div>
        <InviteTeamMemberDialog />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {usersLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 skeleton rounded-md" />
              ))}
            </div>
          ) : (users && Array.isArray(users) && users.length > 0) ? (
            users.map((user: any) => (
              <div key={user.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium">
                      {user.name ? user.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) : '??'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{user.name || 'Unnamed User'}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {user.isActive === false && (
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">Inactive</span>
                  )}
                  <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded capitalize">
                    {user.role || 'member'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Invite team members to collaborate on hiring
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

// Career Page Editor
function CareerPageEditor() {
  const { tenant } = useAuthStore()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)

  const { data: careerPage, isLoading } = useQuery({
    queryKey: ['career-page'],
    queryFn: () => api.get<any>('/tenant/career-page'),
  })

  const [headline, setHeadline] = useState('')
  const [description, setDescription] = useState('')
  const [isPublished, setIsPublished] = useState(false)
  const [seoTitle, setSeoTitle] = useState('')
  const [seoDescription, setSeoDescription] = useState('')
  const [customCss, setCustomCss] = useState('')

  useEffect(() => {
    if (careerPage) {
      setHeadline(careerPage.headline || 'Join Our Team')
      setDescription(careerPage.description || '')
      setIsPublished(careerPage.isPublished || false)
      setSeoTitle(careerPage.seoTitle || '')
      setSeoDescription(careerPage.seoDescription || '')
      setCustomCss(careerPage.customCss || '')
    }
  }, [careerPage])

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.put('/tenant/career-page', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['career-page'] })
      toast({ title: 'Career page saved', variant: 'success' })
      setIsEditing(false)
    },
    onError: () => {
      toast({ title: 'Failed to save career page', variant: 'destructive' })
    },
  })

  const handleSave = () => {
    saveMutation.mutate({
      headline,
      description: description || null,
      isPublished,
      seoTitle: seoTitle || null,
      seoDescription: seoDescription || null,
      customCss: customCss || null,
    })
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="h-32 skeleton rounded-md" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Career Page</CardTitle>
            </div>
            <CardDescription>Customize your public career page</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={isPublished ? 'default' : 'secondary'}>
              {isPublished ? 'Published' : 'Draft'}
            </Badge>
            {!isEditing && (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="published">Published</Label>
              <Switch
                id="published"
                checked={isPublished}
                onCheckedChange={setIsPublished}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="headline">Headline</Label>
              <Input
                id="headline"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Join Our Team"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cp-description">Description</Label>
              <Textarea
                id="cp-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Tell candidates about your company..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seo-title">SEO Title</Label>
              <Input
                id="seo-title"
                value={seoTitle}
                onChange={(e) => setSeoTitle(e.target.value)}
                placeholder="Careers at Company Name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="seo-desc">SEO Description</Label>
              <Textarea
                id="seo-desc"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                rows={2}
                placeholder="Explore career opportunities..."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-css">Custom CSS</Label>
              <Textarea
                id="custom-css"
                value={customCss}
                onChange={(e) => setCustomCss(e.target.value)}
                rows={4}
                className="font-mono text-sm"
                placeholder=".career-page { ... }"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg border bg-muted/50">
              <p className="text-sm">Your career page is available at:</p>
              <a
                href={`/careers/${tenant?.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline"
              >
                ats.app/careers/{tenant?.slug}
              </a>
            </div>
            <div>
              <p className="text-sm font-medium">{headline || 'Join Our Team'}</p>
              {description && <p className="text-sm text-muted-foreground mt-1">{description}</p>}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// Webhooks Manager (6.5)
function WebhooksManager() {
  const queryClient = useQueryClient()
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newUrl, setNewUrl] = useState('')
  const [newEvents, setNewEvents] = useState<string[]>(['*'])
  const [newDescription, setNewDescription] = useState('')

  const webhookEvents = [
    'application.created',
    'application.moved',
    'application.rejected',
    'application.hired',
    'candidate.created',
    'candidate.updated',
    'job.published',
    'job.closed',
    'interview.scheduled',
    'interview.completed',
    '*',
  ]

  const { data: webhooks, isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.get<any[]>('/webhooks'),
  })

  const createMutation = useMutation({
    mutationFn: (data: { url: string; events: string[]; description?: string }) =>
      api.post<any>('/webhooks', data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      toast({
        title: 'Webhook created',
        description: `Secret: ${data.secret} (save this now, it won't be shown again)`,
        variant: 'success',
      })
      setShowCreateDialog(false)
      setNewUrl('')
      setNewEvents(['*'])
      setNewDescription('')
    },
    onError: () => {
      toast({ title: 'Failed to create webhook', variant: 'destructive' })
    },
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/webhooks/${id}`, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] })
      toast({ title: 'Webhook deleted', variant: 'success' })
    },
  })

  const rotateMutation = useMutation({
    mutationFn: (id: string) => api.post<any>(`/webhooks/${id}/rotate-secret`),
    onSuccess: (data) => {
      toast({
        title: 'Secret rotated',
        description: `New secret: ${data.secret} (save this now)`,
        variant: 'success',
      })
    },
  })

  const toggleEvent = (event: string) => {
    setNewEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 skeleton rounded-md" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Webhook className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Webhooks</CardTitle>
          </div>
          <CardDescription>
            Send real-time notifications to external services when events occur
          </CardDescription>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={(isOpen) => {
          setShowCreateDialog(isOpen)
          if (!isOpen) {
            setNewUrl('')
            setNewEvents(['*'])
            setNewDescription('')
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Webhook</DialogTitle>
              <DialogDescription>
                Configure a URL to receive event notifications
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="webhook-url">Endpoint URL</Label>
                <Input
                  id="webhook-url"
                  type="url"
                  placeholder="https://example.com/webhooks"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="webhook-desc">Description (optional)</Label>
                <Input
                  id="webhook-desc"
                  placeholder="Production notification handler"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Events</Label>
                <div className="flex flex-wrap gap-2">
                  {webhookEvents.map((event) => (
                    <Badge
                      key={event}
                      variant={newEvents.includes(event) ? 'default' : 'outline'}
                      className="cursor-pointer"
                      onClick={() => toggleEvent(event)}
                    >
                      {event === '*' ? 'All events' : event}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button
                onClick={() =>
                  createMutation.mutate({
                    url: newUrl,
                    events: newEvents,
                    description: newDescription || undefined,
                  })
                }
                disabled={!newUrl || newEvents.length === 0 || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Webhook'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {(!webhooks || webhooks.length === 0) ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No webhooks configured yet. Create one to start receiving event notifications.
          </p>
        ) : (
          <div className="space-y-3">
            {webhooks.map((wh: any) => (
              <div key={wh.id} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm truncate max-w-[300px]">{wh.url}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={wh.isActive ? 'default' : 'secondary'}>
                      {wh.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Switch
                      checked={wh.isActive}
                      onCheckedChange={(checked) =>
                        toggleMutation.mutate({ id: wh.id, isActive: checked })
                      }
                    />
                  </div>
                </div>
                {wh.description && (
                  <p className="text-xs text-muted-foreground mb-2">{wh.description}</p>
                )}
                <div className="flex flex-wrap gap-1 mb-3">
                  {((wh.events as string[]) || []).map((event: string) => (
                    <Badge key={event} variant="outline" className="text-xs">
                      {event === '*' ? 'All events' : event}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Secret: {wh.secret}</span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => rotateMutation.mutate(wh.id)}
                      disabled={rotateMutation.isPending}
                    >
                      <RotateCw className="h-3 w-3 mr-1" />
                      Rotate Secret
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => {
                        if (confirm('Delete this webhook?')) {
                          deleteMutation.mutate(wh.id)
                        }
                      }}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function SettingsPage() {
  const { tenant } = useAuthStore()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('general')

  const { data: tenantData } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => api.get('/tenant'),
  })

  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<any[]>('/departments'),
  })

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<any[]>('/locations'),
  })

  const [companyName, setCompanyName] = useState(tenant?.name || '')
  const [isCompanyNameSet, setIsCompanyNameSet] = useState(false)

  // Pre-populate company name when data loads
  useEffect(() => {
    if (!isCompanyNameSet) {
      const name = (tenantData as any)?.name || tenant?.name || ''
      if (name) {
        setCompanyName(name)
        setIsCompanyNameSet(true)
      }
    }
  }, [tenantData, tenant, isCompanyNameSet])

  const updateTenant = useMutation({
    mutationFn: (data: { name?: string }) => api.patch('/tenant', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] })
      toast({ title: 'Settings saved', variant: 'success' })
    },
    onError: () => {
      toast({ title: 'Failed to save settings', variant: 'destructive' })
    },
  })

  return (
    <PageLayout fullHeight>
      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        <TabsList className="flex-shrink-0">
          <TabsTrigger value="general">
            <Building className="h-4 w-4 mr-1" />
            General
          </TabsTrigger>
          <TabsTrigger value="organization">
            <Building2 className="h-4 w-4 mr-1" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="team">
            <Users className="h-4 w-4 mr-1" />
            Team
          </TabsTrigger>
          <TabsTrigger value="email">
            <Mail className="h-4 w-4 mr-1" />
            Email
          </TabsTrigger>
          <TabsTrigger value="webhooks">
            <Webhook className="h-4 w-4 mr-1" />
            Webhooks
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="space-y-4 flex-1 min-h-0 overflow-y-auto">
          {/* Company Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-muted-foreground" />
                <CardTitle>Company</CardTitle>
              </div>
              <CardDescription>
                Basic information about your company
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Company URL</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">ats.app/</span>
                  <Input value={tenant?.slug || ''} disabled className="bg-muted" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Contact support to change your company URL
                </p>
              </div>
              <Button
                onClick={() => updateTenant.mutate({ name: companyName })}
                disabled={updateTenant.isPending}
              >
                Save Changes
              </Button>
            </CardContent>
          </Card>

          {/* Career Page */}
          <CareerPageEditor />
        </TabsContent>

        {/* Organization Tab */}
        <TabsContent value="organization" className="space-y-4 flex-1 min-h-0 overflow-y-auto">
          <ResourceManager
            title="Departments"
            description="Organize your jobs by department (e.g., Engineering, Marketing, Sales)"
            icon="department"
            endpoint="/departments"
            queryKey="departments"
            items={(departments as any[]) || []}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['departments'] })}
          />

          <ResourceManager
            title="Locations"
            description="Define office locations for your job postings"
            icon="location"
            endpoint="/locations"
            queryKey="locations"
            items={(locations as any[]) || []}
            onUpdate={() => queryClient.invalidateQueries({ queryKey: ['locations'] })}
          />

          <CustomFieldsManager />
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4 flex-1 min-h-0 overflow-y-auto">
          <TeamTabContent />
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email" className="space-y-4 flex-1 min-h-0 overflow-y-auto">
          <EmailTemplatesManager />
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-4 flex-1 min-h-0 overflow-y-auto">
          <WebhooksManager />
        </TabsContent>
      </Tabs>
    </PageLayout>
  )
}
